import React, { useEffect, FC, useState, useMemo, useCallback } from "react";
import { Modal, ModalBody, Row, Alert, Col, Button } from "reactstrap";
import { FormProvider, useForm } from "react-hook-form";
import FormInput from "../form-components/InputComponent";
import { Icon } from "../../components/Component";
import FormSelect from "../form-components/SelectComponent";
import FormMultiSelect from "../form-components/MultiSelectComponent";
import supplierApis from "../../api/master/supplier";
import sitebreakdownMasterApis from "../../api/master/sitebreakdownMaster";
import brandMasterApis from "../../api/master/brandMaster";
import Swal from "sweetalert2";
interface SelectOptions {
  label: string;
  value: any;
}

const SEGMENT_TO_WORK_LEVEL: Record<string, string> = {
  Tower: "TOWER_BLOCK",
  "Tower/Block": "TOWER_BLOCK",
  "Tower / Block": "TOWER_BLOCK",
  "Tower/ Block": "TOWER_BLOCK",
  Block: "TOWER_BLOCK",
  Villa: "VILLA",
  Floor: "FLOOR",
  Flat: "FLAT",
};

const WORK_LEVEL_TO_SEGMENT: Record<string, string> = {
  TOWER_BLOCK: "Tower",
  VILLA: "Villa",
  FLOOR: "Floor",
  FLAT: "Flat",
};

const WORK_LEVEL_OPTIONS: SelectOptions[] = [
  { label: "Tower / Block", value: "TOWER_BLOCK" },
  { label: "Villa", value: "VILLA" },
  { label: "Floor", value: "FLOOR" },
  { label: "Flat", value: "FLAT" },
];

const MaterialModal: FC<any> = ({
  modal,
  closeModal,
  append,
  update,
  formData,
  status,
  materialCategoryOptions,
  materialsList,
  orderContext,
  boqData = [],
}) => {
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);

  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [materialData, setMaterialData] = useState<any>();
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [towerOptions, setTowerOptions] = useState<SelectOptions[]>([]);
  const [floorOptions, setFloorOptions] = useState<SelectOptions[]>([]);
  const [flatOptions, setFlatOptions] = useState<SelectOptions[]>([]);
  const [locationOptions, setLocationOptions] = useState<SelectOptions[]>([]);
  const [isTowerLoading, setIsTowerLoading] = useState(false);
  const [isFloorLoading, setIsFloorLoading] = useState(false);
  const [isFlatLoading, setIsFlatLoading] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [makeOptions, setMakeOptions] = useState<SelectOptions[]>([]);
  const [towerFloorCounts, setTowerFloorCounts] = useState<Record<string, number>>({});

  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    setValue,
  } = methods;
  const workLevelSettingWatch = watch("workLevelSetting");
  const towerBlockVillaIdWatch = watch("towerBlockVillaId");
  const floorNoWatch = watch("floorNo");
  const flatIdWatch = watch("flatId");
  const locationIdWatch = watch("locationId");

  const [qtyExceededError, setQtyExceededError] = useState<string>("");
  const materialNameWatch = watch("materialName");
  const quantityWatch = watch("quantity");

  // Compute prRemainingQty for the selected material from boqData
  const matchedBoqItem = useMemo(() => {
    if (!Array.isArray(boqData) || !boqData.length || !materialNameWatch) return null;
    return boqData.find(
      (boq: any) =>
        (boq.materialId?.toString() || boq.materialID?.toString()) ===
        materialNameWatch?.toString()
    ) || null;
  }, [boqData, materialNameWatch]);

  const prRemainingQty = useMemo(() => {
    if (!matchedBoqItem) return null;
    const qty = parseFloat(matchedBoqItem.prRemainingQty);
    return isNaN(qty) ? null : qty;
  }, [matchedBoqItem]);

  // Initial quantity from the existing line item (only for edit mode)
  const initialQty = useMemo(() => {
    if (formData?.action === "edit" && formData?.item?.quantity != null) {
      return parseFloat(formData.item.quantity) || 0;
    }
    return 0;
  }, [formData?.action, formData?.item?.quantity]);

  // Show/hide inline max allowed qty info when quantity changes
  // maxAllowed = max(prRemainingQty + initialQty, 0)
  const maxAllowedQty = useMemo(() => {
    if (prRemainingQty == null) return null;
    const val = prRemainingQty + initialQty;
    return val > 0 ? val : 0;
  }, [prRemainingQty, initialQty]);

  useEffect(() => {
    if (maxAllowedQty != null) {
      const enteredQty = parseFloat(quantityWatch) || 0;
      // Only show error when user has increased qty from initial AND it exceeds max allowed
      if (enteredQty > initialQty && enteredQty > maxAllowedQty) {
        setQtyExceededError(
          `Max allowed: ${maxAllowedQty}`
        );
      } else {
        setQtyExceededError("");
      }
    } else {
      setQtyExceededError("");
    }
  }, [quantityWatch, maxAllowedQty, initialQty]);

  const [alertInfo, setAlertInfo] = useState({
    type: "", // 'success' or 'error'
    message: "",
    errors: [],
  });

  // Helper to check if a flat value is present
  const hasFlatValue = (item: any) => {
    const fId = item?.flatId ?? item?.siteStructureSubUnitId;
    return fId != null && fId !== "" && fId !== "null" && fId !== "undefined";
  };

  const orderSiteId = orderContext?.siteId ?? null;
  const orderSegmentLevel = orderContext?.segmentLevel ?? null;
  const orderTowerId = orderContext?.towerBlockVillaId ?? null;
  const orderFloorNo = orderContext?.floorNo ?? null;
  const orderFlatId =
    orderContext?.flatId ?? orderContext?.siteStructureSubUnitId ?? null;
  const orderLocationId = orderContext?.locationId ?? null;

  const orderWorkLevelSetting = useMemo(() => {
    if (
      orderContext?.workLevelSetting &&
      orderContext?.workLevelSetting !== "NONE"
    ) {
      return orderContext.workLevelSetting;
    }
    if (orderSegmentLevel) {
      return SEGMENT_TO_WORK_LEVEL[orderSegmentLevel] ?? null;
    }
    return null;
  }, [orderContext?.workLevelSetting, orderSegmentLevel]);

  const shouldShowHierarchy =
    workLevelSettingWatch && workLevelSettingWatch !== "NONE" && orderSiteId;

  const hierarchyFieldLabel = useMemo(() => {
    switch (workLevelSettingWatch) {
      case "VILLA":
        return "Villa";
      case "TOWER_BLOCK":
      case "FLOOR":
      case "FLAT":
        return "Tower / Block";
      default:
        return "Tower / Block / Villa";
    }
  }, [workLevelSettingWatch]);

  const hierarchyFieldPlaceholder = useMemo(() => {
    switch (workLevelSettingWatch) {
      case "VILLA":
        return "Select Villa";
      case "TOWER_BLOCK":
      case "FLOOR":
      case "FLAT":
        return "Select Tower / Block";
      default:
        return "Select Tower / Block / Villa";
    }
  }, [workLevelSettingWatch]);

  // Helper to check if an item should be excluded (current edit item)
  const isEditExcluded = (item: any) => {
    if (formData.action !== "edit") return false;

    // PRIORITY 1: Match by client-side rowUid — unique per row even for newly
    // added rows that have no prLineSiteStructureId / prLineId yet.
    if (
      formData.item?.rowUid != null &&
      item.rowUid != null &&
      formData.item.rowUid === item.rowUid
    ) {
      return true;
    }

    // PRIORITY 2: Match by prLineSiteStructureId (unique identifier after flattening)
    // After siteStructures[] are flattened, each row has unique prLineSiteStructureId
    // but shares the same prLineId, so we must use prLineSiteStructureId for proper identification
    if (
      formData.item?.prLineSiteStructureId != null &&
      item.prLineSiteStructureId != null &&
      formData.item.prLineSiteStructureId == item.prLineSiteStructureId
    ) {
      return true;
    }

    // PRIORITY 3: Match by exact field comparison (for newly added items or fallback)
    // Newly added items (not saved yet) don't have prLineSiteStructureId
    if (formData.item) {
      return (
        item.materialId == formData.item.materialId &&
        (item.siteStructureUnitId ?? item.towerBlockVillaId) == (formData.item.siteStructureUnitId ?? formData.item.towerBlockVillaId) &&
        item.floorNo == formData.item.floorNo &&
        (item.flatId ?? item.siteStructureSubUnitId) == (formData.item.flatId ?? formData.item.siteStructureSubUnitId) &&
        item.locationId == formData.item.locationId
      );
    }

    return false;
  };

  const wls = workLevelSettingWatch ?? orderWorkLevelSetting;

  // Filter tower options: hide towers where the same material is already added
  const filteredTowerOptions = useMemo(() => {
    if (!materialNameWatch) return towerOptions;
    const existingItems = Array.isArray(materialsList) ? materialsList : [];

    const blockedTowers = new Set<any>();

    existingItems.forEach((item: any) => {
      if (isEditExcluded(item)) return; // Skip current item being edited
      if (item.materialId != materialNameWatch) return; // Different material

      // Block tower/villa at different work levels
      if (wls === "TOWER_BLOCK" || wls === "VILLA") {
        // TOWER/VILLA level: block if same material + same tower exists
        const towerValue = item.siteStructureUnitId ?? item.towerBlockVillaId;
        if (towerValue != null) {
          blockedTowers.add(towerValue);
        }
      } else if (wls === "FLOOR") {
        // FLOOR level: only block tower if no floor specified (tower-level entry)
        const towerValue = item.siteStructureUnitId ?? item.towerBlockVillaId;
        const floorValue = item.floorNo;
        if (towerValue != null && (!floorValue || floorValue === "")) {
          blockedTowers.add(towerValue);
        }
        // If floor IS specified → tower is available for other floors ✅
      }
    });

    // NEW: Hierarchical progression validation for FLOOR work level
    // For FLAT level: do NOT block tower based on floor counts,
    // because individual floors may still have available flats
    if (wls === "FLOOR") {
      towerOptions.forEach((towerOption) => {
        const towerId = towerOption.value;

        // Rule 1: Check if ANY floor-level entries exist for this material+tower
        // If yes, prevent adding tower-level entry (hierarchy consistency)
        const hasFloorLevelEntries = existingItems.some(
          (item: any) =>
            !isEditExcluded(item) &&
            item.materialId == materialNameWatch &&
            (item.siteStructureUnitId ?? item.towerBlockVillaId) == towerId &&
            item.floorNo != null &&
            item.floorNo !== ""
        );

        if (hasFloorLevelEntries) {
          // Count how many unique floors are used for this material+tower
          const usedFloors = new Set<string>();
          existingItems.forEach((item: any) => {
            if (
              !isEditExcluded(item) &&
              item.materialId == materialNameWatch &&
              (item.siteStructureUnitId ?? item.towerBlockVillaId) == towerId &&
              item.floorNo != null &&
              item.floorNo !== ""
            ) {
              usedFloors.add(item.floorNo);
            }
          });

          // Rule 2: Block tower if all floors are consumed
          // Check cached floor count for this tower
          const totalFloorsAvailable = towerFloorCounts[String(towerId)];
          if (totalFloorsAvailable !== undefined && usedFloors.size >= totalFloorsAvailable) {
            // All floors for this tower are used for this material
            // Block the tower completely (can't add more)
            blockedTowers.add(towerId);
          }
          // Note: If floor count not cached yet, tower remains available
          // It will be blocked after user selects tower and floors are loaded
        }
      });
    }

    return towerOptions.filter((opt) => !blockedTowers.has(opt.value));
  }, [towerOptions, materialNameWatch, materialsList, formData, wls, towerFloorCounts]);

  // Filter floor options based on hierarchy level
  const filteredFloorOptions = useMemo(() => {
    if (!materialNameWatch || !towerBlockVillaIdWatch) return floorOptions;
    const existingItems = Array.isArray(materialsList) ? materialsList : [];

    const blockedFloors = new Set<any>();
    existingItems.forEach((item: any) => {
      if (isEditExcluded(item)) return;
      if (
        item.materialId != materialNameWatch ||
        (item.siteStructureUnitId != towerBlockVillaIdWatch && item.towerBlockVillaId != towerBlockVillaIdWatch)
      ) return;

      // Block floor in these cases:
      // 1. FLOOR level: if same material + tower + floor exists
      // 2. FLAT level: if floor-level entry exists (no flat specified)
      // Note: Floor is now optional, only block if it has a value

      if (wls === "FLOOR") {
        // Block floor if it exists with this material at FLOOR or FLAT level
        if (item.floorNo != null && item.floorNo !== "") {
          blockedFloors.add(item.floorNo);
        }
      } else if (wls === "FLAT") {
        // Block floor if floor-level entry exists (covers all flats)
        if (item.floorNo != null && item.floorNo !== "" && !hasFlatValue(item)) {
          blockedFloors.add(item.floorNo);
        }
      }
    });

    return floorOptions.filter((opt) => !blockedFloors.has(opt.value));
  }, [floorOptions, materialNameWatch, towerBlockVillaIdWatch, materialsList, formData, wls]);

  // Filter flat options: hide flats where same material already added
  const filteredFlatOptions = useMemo(() => {
    if (!materialNameWatch || !towerBlockVillaIdWatch) return flatOptions;
    const existingItems = Array.isArray(materialsList) ? materialsList : [];

    // If floor-level entry exists for this material, all flats are blocked
    const isFloorLevelBlocked = existingItems.some((item: any) => {
      if (isEditExcluded(item)) return false;
      return (
        item.materialId == materialNameWatch &&
        (item.siteStructureUnitId == towerBlockVillaIdWatch || item.towerBlockVillaId == towerBlockVillaIdWatch) &&
        item.floorNo == floorNoWatch &&
        !hasFlatValue(item)
      );
    });

    if (isFloorLevelBlocked) return [];

    const blockedFlats = new Set<any>();
    existingItems.forEach((item: any) => {
      if (isEditExcluded(item)) return;
      if (
        item.materialId == materialNameWatch &&
        (item.siteStructureUnitId == towerBlockVillaIdWatch || item.towerBlockVillaId == towerBlockVillaIdWatch) &&
        item.floorNo == floorNoWatch &&
        hasFlatValue(item)
      ) {
        blockedFlats.add(item.flatId ?? item.siteStructureSubUnitId);
      }
    });

    return flatOptions.filter((opt) => !blockedFlats.has(opt.value));
  }, [flatOptions, materialNameWatch, towerBlockVillaIdWatch, floorNoWatch, materialsList, formData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
    }, 2500);
    return () => clearTimeout(timeoutId);
  }, [alertInfo]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  const loadTowerOptions = useCallback(async () => {
    if (!shouldShowHierarchy || !orderSiteId) {
      setTowerOptions([]);
      return;
    }
    setIsTowerLoading(true);
    try {
      const response = await sitebreakdownMasterApis.fetchUnitsBySiteId({
        siteId: orderSiteId,
      });
      if (response?.status && Array.isArray(response.data)) {
        setTowerOptions(
          response.data.map((unit: any) => ({
            value: unit.id,
            label: unit.unitName,
          }))
        );
      } else {
        setTowerOptions([]);
      }
    } catch (error) {
      console.log("Failed to load tower options", error);
      setTowerOptions([]);
    } finally {
      setIsTowerLoading(false);
    }
  }, [orderSiteId, shouldShowHierarchy]);

  const loadFloorOptions = useCallback(
    async (unitId?: number | string) => {
      if (
        !shouldShowHierarchy ||
        !unitId ||
        (workLevelSettingWatch !== "FLOOR" && workLevelSettingWatch !== "FLAT")
      ) {
        setFloorOptions([]);
        return;
      }
      setIsFloorLoading(true);
      try {
        const response =
          await sitebreakdownMasterApis.fetchFloorNumbersByUnitId({
            siteId: orderSiteId,
            unitId,
          });
        if (response?.status && Array.isArray(response.data)) {
          const floors = response.data.map((floor: any) => ({
            value: floor.floorNo,
            label: floor.floorNo,
          }));
          setFloorOptions(floors);

          // Cache floor count for this tower
          setTowerFloorCounts(prev => ({
            ...prev,
            [String(unitId)]: floors.length
          }));
        } else {
          setFloorOptions([]);
        }
      } catch (error) {
        console.log("Failed to load floor options", error);
        setFloorOptions([]);
      } finally {
        setIsFloorLoading(false);
      }
    },
    [orderSiteId, shouldShowHierarchy, workLevelSettingWatch]
  );

  const loadFlatOptions = useCallback(
    async (unitId?: number | string, floorNo?: string | null) => {
      if (!shouldShowHierarchy || workLevelSettingWatch !== "FLAT" || !unitId) {
        setFlatOptions([]);
        return;
      }
      setIsFlatLoading(true);
      try {
        const response = await sitebreakdownMasterApis.fetchSubUnitsByUnitId({
          siteId: orderSiteId,
          unitId,
          floorNo: floorNo ?? undefined,
        });
        if (response?.status && Array.isArray(response.data)) {
          setFlatOptions(
            response.data.map((flat: any) => ({
              value: flat.id,
              label: flat.subUnitName || flat.unitName,
            }))
          );
        } else {
          setFlatOptions([]);
        }
      } catch (error) {
        console.log("Failed to load flat options", error);
        setFlatOptions([]);
      } finally {
        setIsFlatLoading(false);
      }
    },
    [orderSiteId, shouldShowHierarchy, workLevelSettingWatch]
  );

  const loadLocationOptions = useCallback(async () => {
    if (!orderSiteId) {
      setLocationOptions([]);
      return;
    }
    setIsLocationLoading(true);
    try {
      const response = await sitebreakdownMasterApis.fetchLocationsBySiteId({
        siteId: orderSiteId,
      });
      if (response?.status && Array.isArray(response.data)) {
        const options = response.data.map((location: any) => ({
          value: location.id,
          label: location.locationName,
        }));
        setLocationOptions(options);
      } else {
        setLocationOptions([]);
      }
    } catch (error) {
      console.log("Failed to load location options", error);
      setLocationOptions([]);
    } finally {
      setIsLocationLoading(false);
    }
  }, [locationIdWatch, orderSiteId, setValue]);

  useEffect(() => {
    if (!modal) {
      return;
    }
    if (shouldShowHierarchy) {
      loadTowerOptions();
      loadLocationOptions();
    } else {
      setTowerOptions([]);
      setFloorOptions([]);
      setFlatOptions([]);
      setLocationOptions([]);
      setValue("towerBlockVillaId", null);
      setValue("floorNo", null);
      setValue("flatId", null);
    }
  }, [
    loadLocationOptions,
    loadTowerOptions,
    modal,
    setValue,
    shouldShowHierarchy,
  ]);

  useEffect(() => {
    if (!modal || !shouldShowHierarchy) {
      setFloorOptions([]);
      setValue("floorNo", null);
      return;
    }
    if (
      (workLevelSettingWatch === "FLOOR" || workLevelSettingWatch === "FLAT") &&
      towerBlockVillaIdWatch
    ) {
      loadFloorOptions(towerBlockVillaIdWatch);
    } else if (workLevelSettingWatch !== "FLAT") {
      setFloorOptions([]);
      setValue("floorNo", null);
    }
  }, [
    loadFloorOptions,
    modal,
    setValue,
    shouldShowHierarchy,
    towerBlockVillaIdWatch,
    workLevelSettingWatch,
  ]);

  useEffect(() => {
    if (!modal || !shouldShowHierarchy) {
      setFlatOptions([]);
      setValue("flatId", null);
      return;
    }
    if (workLevelSettingWatch === "FLAT" && towerBlockVillaIdWatch) {
      loadFlatOptions(towerBlockVillaIdWatch, floorNoWatch ?? null);
    } else {
      setFlatOptions([]);
      setValue("flatId", null);
    }
  }, [
    floorNoWatch,
    loadFlatOptions,
    modal,
    setValue,
    shouldShowHierarchy,
    towerBlockVillaIdWatch,
    workLevelSettingWatch,
  ]);

  useEffect(() => {
    if (modal && formData.item) {
      console.log("formData?.item", formData?.item);

      const {
        categoryId,
        materialId,
        materialName,
        quantity,
        specification,
        uomId,
        remarks,
        description,
        actualStatus,
      } = formData.item;
      console.log(formData.item);
      setValue("materialCategory", categoryId);
      (async () => {
        const _materialData = await fetchMaterials(categoryId, materialId);
        await Promise.all([
          fetchUom(materialId, _materialData, uomId),
          fetchMakeOptions(materialId),
        ]);
        if (formData.item?.makeInfo?.length > 0) {
          setValue("make", formData.item.makeInfo.map((m: any) => Number(m.makeId)));
        }
      })();
      setValue("description", description);
      setValue("quantity", Number(quantity));
      setValue("specification", specification);
      setValue("remarks", remarks);
      setValue("actualStatus", actualStatus);
    }
    setQtyExceededError("");
  }, [modal]);

  useEffect(() => {
    if (!modal) {
      return;
    }
    const lineItem = formData?.item || {};
    const derivedWorkLevel =
      (lineItem?.workLevelSetting &&
        lineItem.workLevelSetting !== "NONE" &&
        lineItem.workLevelSetting) ||
      (lineItem?.segmentLevel
        ? SEGMENT_TO_WORK_LEVEL[lineItem.segmentLevel] ?? null
        : null) ||
      orderWorkLevelSetting ||
      "NONE";
    setValue("workLevelSetting", derivedWorkLevel || "NONE");
    setValue(
      "towerBlockVillaId",
      lineItem?.siteStructureUnitId ?? orderTowerId ?? null
    );
    setValue("floorNo", lineItem?.floorNo ?? orderFloorNo ?? null);
    setValue("flatId", lineItem?.siteStructureSubUnitId ?? orderFlatId ?? null);
    setValue("locationId", lineItem?.locationId ?? orderLocationId ?? null);
  }, [
    formData?.item,
    modal,
    orderFlatId,
    orderFloorNo,
    orderLocationId,
    orderTowerId,
    orderWorkLevelSetting,
    setValue,
  ]);

  // Helper to get floor count for a tower (from cache or fetch)
  const getFloorCountForTower = useCallback(
    async (towerId: number | string): Promise<number> => {
      const towerIdStr = String(towerId);

      // Return cached value if available
      if (towerFloorCounts[towerIdStr] !== undefined) {
        return towerFloorCounts[towerIdStr];
      }

      // Fetch from API if not cached
      try {
        const response = await sitebreakdownMasterApis.fetchFloorNumbersByUnitId({
          siteId: orderSiteId,
          unitId: towerId,
        });
        if (response?.status && Array.isArray(response.data)) {
          const count = response.data.length;
          setTowerFloorCounts(prev => ({
            ...prev,
            [towerIdStr]: count
          }));
          return count;
        }
      } catch (error) {
        console.log("Failed to fetch floor count for tower", towerId, error);
      }
      return 0;
    },
    [orderSiteId, towerFloorCounts]
  );

  // Pre-fetch floor counts for towers with floor-level entries
  useEffect(() => {
    if (
      !materialsList ||
      !Array.isArray(materialsList) ||
      (wls !== "FLOOR" && wls !== "FLAT")
    ) {
      return;
    }

    // Collect all towers that have floor-level entries
    const towersWithFloors = new Set<string>();
    materialsList.forEach((item: any) => {
      const towerId = item.siteStructureUnitId ?? item.towerBlockVillaId;
      if (towerId && item.floorNo != null && item.floorNo !== "") {
        towersWithFloors.add(String(towerId));
      }
    });

    // Fetch floor counts for towers not yet cached
    towersWithFloors.forEach(async (towerId) => {
      if (towerFloorCounts[towerId] === undefined) {
        await getFloorCountForTower(towerId);
      }
    });
  }, [materialsList, wls, towerFloorCounts, getFloorCountForTower]);

  const submitForm = (data: any) => {
    const {
      materialCategory,
      materialName,
      quantity,
      specification,
      uomId,
      remarks,
      description,
      make,
      workLevelSetting,
      towerBlockVillaId,
      floorNo,
      flatId,
      locationId,
    } = data;

    // Validate quantity against prRemainingQty from BOQ data
    // maxAllowed = max(prRemainingQty + savedQty, 0)
    if (Array.isArray(boqData) && boqData.length > 0) {
      const matched = boqData.find(
        (boq: any) =>
          (boq.materialId?.toString() || boq.materialID?.toString()) ===
          materialName?.toString()
      );
      if (matched) {
        const enteredQty = parseFloat(quantity) || 0;
        const remainingQty = parseFloat(matched.prRemainingQty) || 0;
        const savedQty = formData?.action === "edit" ? (parseFloat(formData?.item?.quantity) || 0) : 0;
        const maxAllowed = Math.max(remainingQty + savedQty, 0);
        if (enteredQty > savedQty && enteredQty > maxAllowed) {
          setQtyExceededError(`Max allowed: ${maxAllowed}`);
          Swal.fire({
            icon: "error",
            title: "Quantity Exceeded",
            text: `Entered quantity exceeds the maximum allowed quantity (${maxAllowed}). Please adjust the quantity.`,
          });
          return;
        }
      }
    }
    setQtyExceededError("");

    const resolvedWorkLevelSetting =
      workLevelSetting && workLevelSetting !== "NONE" ? workLevelSetting : null;
    const resolvedSegmentLevel = resolvedWorkLevelSetting
      ? WORK_LEVEL_TO_SEGMENT[resolvedWorkLevelSetting] ?? null
      : null;

    const towerLabel =
      towerOptions.find((option) => option.value == towerBlockVillaId)?.label ||
      formData?.item?.towerBlockVillaName ||
      formData?.item?.siteStructureUnitName ||
      "";
    const flatLabel = flatId && flatId !== "null" && flatId !== null && flatId !== ""
      ? flatOptions.find((option) => option.value == flatId)?.label ||
      formData?.item?.flatLabel ||
      formData?.item?.siteStructureSubUnitName ||
      ""
      : "";
    const locationLabel =
      locationOptions.find((option) => option.value == locationId)?.label ||
      formData?.item?.locationLabel ||
      formData?.item?.locationName ||
      "";

    const actualStatus = formData?.item?.actualStatus || "Open";

    const selectedMakeIds = Array.isArray(make) ? make : [];
    const makeInfoData = selectedMakeIds
      .map((id: any) => ({
        makeId: Number(id),
        makeName: makeOptions.find((item) => item.value == id)?.label || "",
      }))
      .filter((m: any) => m.makeId && m.makeName);

    const formResult = {
      categoryId: Number(materialCategory),
      materialId: Number(materialName),
      materialName: materialOptions.find((item) => item.value == materialName)
        ?.label,
      categoryName: materialCategoryOptions.find(
        (item: any) => item.value == materialCategory
      )?.label,
      uomId: Number(uomId),
      uomName: uomOptions.find((item) => item.value == uomId)?.label,
      quantity: Number(quantity),
      specification,
      remarks,
      description: description ?? formData?.item?.description,
      makeInfo: makeInfoData,
      actualStatus,
      workLevelSetting: resolvedWorkLevelSetting,
      segmentLevel: resolvedSegmentLevel,
      siteStructureUnitId: towerBlockVillaId ? Number(towerBlockVillaId) : null,
      towerBlockVillaId: towerBlockVillaId ? Number(towerBlockVillaId) : null,
      towerBlockVillaName: towerLabel,
      siteStructureUnitName: towerLabel,
      floorNo: floorNo ?? null,
      siteStructureSubUnitId: flatId && flatId !== "null" && flatId !== null && flatId !== ""
        ? Number(flatId)
        : null,
      flatId: flatId && flatId !== "null" && flatId !== null && flatId !== ""
        ? Number(flatId)
        : null,
      siteStructureSubUnitName: flatLabel,
      flatLabel,
      locationId: locationId ? Number(locationId) : null,
      locationName: locationLabel,
      locationLabel,
    };

    // Hierarchy-aware duplicate validation
    const existingItems = Array.isArray(materialsList) ? materialsList : [];
    let duplicateMessage = "";

    const newHasFlat = hasFlatValue({ flatId, siteStructureSubUnitId: flatId });

    for (const item of existingItems) {
      if (isEditExcluded(item)) continue;
      if (item.materialId != materialName) continue;
      if (item.locationId != (locationId ?? null)) continue;

      const itemTowerId = item.siteStructureUnitId ?? item.towerBlockVillaId;
      const newTowerId = towerBlockVillaId ?? null;

      // VILLA / TOWER_BLOCK: same tower/villa = duplicate
      if (wls === "VILLA" || wls === "TOWER_BLOCK") {
        if (itemTowerId == newTowerId) {
          duplicateMessage = "This material already exists for the selected site hierarchy.";
          break;
        }
        continue;
      }

      // FLOOR / FLAT flow
      if (wls === "FLOOR" || wls === "FLAT") {
        if (itemTowerId != newTowerId || item.floorNo != (floorNo ?? null)) continue;

        const existingHasFlat = hasFlatValue(item);

        if (!newHasFlat) {
          duplicateMessage = existingHasFlat
            ? "Material already exists in a flat on this floor. Cannot add at floor level."
            : "This material already exists for the selected site hierarchy.";
          break;
        }

        if (!existingHasFlat) {
          duplicateMessage = "Material already added at floor level, which covers all flats.";
          break;
        }

        const existingFlatId = item.flatId ?? item.siteStructureSubUnitId;
        if (existingFlatId == (flatId ?? null)) {
          duplicateMessage = "This material already exists for the selected site hierarchy.";
          break;
        }
      }
    }

    if (duplicateMessage) {
      setAlertInfo({
        type: "error",
        message: duplicateMessage,
        errors: [],
      });
      return;
    }

    if (formData.action == "add" || !formData.index == null) {
      append({
        ...formResult,
        prLineId: null,
        prLineSiteStructureId: null,
      });
    } else {
      update(formData.index, {
        ...formResult,
        prLineId: formData?.item?.prLineId,
        prLineSiteStructureId: formData?.item?.prLineSiteStructureId || null,
      });
    }
    closeModal();
  };

  const fetchMakeOptions = async (materialId: any) => {
    if (!materialId) {
      setMakeOptions([]);
      return;
    }
    try {
      const response = await brandMasterApis.fetchBrandById(materialId);
      if (response?.status && response?.data?.brands) {
        const options = response.data.brands
          .filter((brand: any) => brand.status?.toUpperCase() === "ACTIVE")
          .map((brand: any) => ({
            label: brand.make,
            value: brand.id,
          }));
        setMakeOptions(options);
      } else {
        setMakeOptions([]);
      }
    } catch (error) {
      console.log("Failed to fetch make options", error);
      setMakeOptions([]);
    }
  };

  const fetchUom = async (materialId: any, materialData: any, uomId: any) => {
    try {
      const materialUom = materialData?.find(
        (item: any) => item.materialId == materialId
      );
      if (!materialUom) return;
      const _uomOptions = [
        {
          value: materialUom?.primaryUomId,
          label: materialUom?.PrimaryUomName,
        },
      ];
      if (
        materialUom?.secondaryUomId &&
        materialUom?.primaryUomId != materialUom?.secondaryUomId &&
        materialUom?.PrimaryUomName != materialUom?.secondaryUomName
      ) {
        _uomOptions.push({
          value: materialUom?.secondaryUomId,
          label: materialUom?.secondaryUomName,
        });
      }
      setValue("uomId", uomId ? uomId : materialUom?.primaryUomId);
      setUomOptions(_uomOptions);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterials = async (categoryId: any, materialId: any) => {
    try {
      const { data, status } = await supplierApis.fetchCategoryProducts(
        categoryId,
        orderSiteId
      );
      if (status) {
        const materialOption = data.map((item: any) => ({
          label: item?.materialName,
          value: item?.materialId?.toString(),
        }));
        console.log(materialsList);
        if (
          (formData.action === "add" || formData.index === null) &&
          !(orderSegmentLevel && orderSiteId)
        ) {
          if (Array.isArray(materialOption)) {
            const filteredOptions = materialOption.filter(
              (option) =>
                !materialsList || !Array.isArray(materialsList) || !materialsList.some((row: any) => row?.materialId?.toString() === option.value)
            );
            setMaterialOptions(filteredOptions);
          }
        } else {
          setMaterialOptions(materialOption);
        }
        setMaterialData(data);
        setValue("materialName", materialId);
        return data;
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Modal isOpen={modal} toggle={closeModal} size="xl">
      <ModalBody>
        <h5 className="title">
          {formData.action == "add" || formData.index == null
            ? "Add New Line"
            : "Update Line"}
        </h5>
        <div style={{ marginTop: "15px" }}></div>
        {alertInfo.message && (
          <div className="mb-3 mt-1">
            <Alert
              color={alertInfo.type === "error" ? "danger" : "success"}
              className="alert-icon"
            >
              <strong>{alertInfo.message}</strong>
              <Icon
                name={
                  alertInfo.type === "error" ? "alert-circle" : "check-circle"
                }
              />
              {alertInfo.errors.length > 0 ? (
                <ul>
                  {alertInfo.errors.map((err: any, index: any) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              ) : (
                ""
              )}
            </Alert>
          </div>
        )}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)}>
            <Row className="mt-12">
              <div className="col-md-4">
                <FormSelect
                  name="materialCategory"
                  label="Material Category"
                  required={true}
                  options={materialCategoryOptions}
                  placeholder="Select Material Category"
                  onChange={(e: any) => {
                    fetchMaterials(e, null);
                    setMakeOptions([]);
                    setValue("make", []);
                  }}
                />
              </div>
              <div className="col-md-4">
                <FormSelect
                  name="materialName"
                  label="Material Name"
                  required={true}
                  options={materialOptions}
                  onChange={async (e: any) => {
                    setValue("make", []);
                    await Promise.all([
                      fetchUom(e, materialData, null),
                      fetchMakeOptions(e),
                    ]);
                  }}
                  placeholder="Select Material Name"
                />
              </div>
              <div className="col-md-4">
                <FormMultiSelect
                  name="make"
                  label="Make"
                  options={makeOptions}
                  placeholder="Select Make"
                />
              </div>
            </Row>
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              <div className="col-md-3">
                <FormInput
                  type="number"
                  min={0.1}
                  required={true}
                  name="quantity"
                  label="Required Quantity"
                  placeholder="Enter Quantity"
                />
              </div>
              <div className="col-md-3">
                <FormSelect
                  name="uomId"
                  label="UOM"
                  required={true}
                  options={uomOptions}
                  placeholder="Units"
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  type="text"
                  name="specification"
                  label="Specification"
                  placeholder="Enter Specification"
                />
              </div>
            </Row>
            {qtyExceededError && (
              <div style={{ color: "#ff4d4f", fontSize: 13, marginTop: 4, marginBottom: 4 }}>
                {qtyExceededError}
              </div>
            )}
            <Row className="mt-4">
              <div className="col-md-6">
                <FormInput
                  type="text"
                  required={false}
                  min={1}
                  name="remarks"
                  label="Remarks (if any)"
                  placeholder="Enter Remarks"
                />
              </div>
              {/* <div className="col-md-12">
                                <FormTextArea
                                    name="description"
                                    required={false}
                                    label="Description (if any)"
                                    placeholder="Enter Description"
                                    minLength={5}
                                    maxLength={200}
                                />
                            </div> */}
            </Row>
            {shouldShowHierarchy && (
              <Row className="mt-4">
                <div className="col-md-3">
                  <FormSelect
                    name="towerBlockVillaId"
                    label={hierarchyFieldLabel}
                    options={filteredTowerOptions}
                    placeholder={hierarchyFieldPlaceholder}
                    required
                    isLoading={isTowerLoading}
                  />
                </div>
                {(workLevelSettingWatch === "FLOOR" ||
                  workLevelSettingWatch === "FLAT") && (
                    <div className="col-md-3">
                      <FormSelect
                        name="floorNo"
                        label="Floor"
                        options={filteredFloorOptions}
                        placeholder="Select Floor"
                        required={false}
                        isLoading={isFloorLoading}
                      />
                    </div>
                  )}
                {workLevelSettingWatch === "FLAT" && (
                  <div className="col-md-3">
                    <FormSelect
                      name="flatId"
                      label="Flat"
                      options={filteredFlatOptions}
                      placeholder="Select Flat"
                      required={false}
                      isLoading={isFlatLoading}
                    />
                  </div>
                )}
                <div className="col-md-3 mt-3 mt-md-0">
                  <FormSelect
                    name="locationId"
                    label="Location"
                    options={locationOptions}
                    placeholder="Select Location"
                    isLoading={isLocationLoading}
                  />
                </div>
              </Row>
            )}
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  {formData.action === "edit" && (
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="submit"
                        color="primary"
                      >
                        Update
                      </Button>
                    </li>
                  )}
                  {formData.action == "add" && (
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="submit"
                        color="primary"
                      >
                        Save
                      </Button>
                    </li>
                  )}
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        closeModal();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          </form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

export default MaterialModal;
